import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, ArrowLeft, Copy, Check, Users, Link, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from '../lib/apiTransport';
import { useSafeBack } from '../hooks/useSafeBack';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

/**
 * CreateClub - Host club creation flow
 * 
 * Phase 10A.1: Geography Normalization
 * - Google Places Autocomplete biased toward cities/suburbs/neighborhoods
 * - Manual typing fallback enabled
 * - Stores location_label (display), google_place_id, lat, lng
 */
const CreateClub = () => {
  const navigate = useNavigate();
  const { goBack } = useSafeBack('/host');
  const { token, user } = useContext(AuthContext);
  
  const [name, setName] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdClub, setCreatedClub] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  
  // Phase 10A.1: Geo metadata from Places Autocomplete
  const [googlePlaceId, setGooglePlaceId] = useState(null);
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [autocompleteLoaded, setAutocompleteLoaded] = useState(false);

  // Unsaved-changes protection (only before the club is created).
  const isDirty = Boolean(!createdClub && (name.trim() || locationLabel.trim()));
  const { promptOpen, confirmLeave, cancelLeave, guardedLeave } = useUnsavedChangesGuard({
    active: isDirty,
    shouldPrompt: isDirty,
  });
  
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  // Load Google Maps Places API
  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) {
      return;
    }
    
    // Check if already loaded
    if (window.google?.maps?.places) {
      setAutocompleteLoaded(true);
      return;
    }
    
    // Load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setAutocompleteLoaded(true);
    };
    script.onerror = () => {
      console.error('[CreateClub] Failed to load Google Maps API');
      console.error('[CreateClub] Failed to load Google Maps API');
    };
    document.head.appendChild(script);
    
    return () => {
      // Cleanup not needed - script persists
    };
  }, []);
  
  // Initialize Autocomplete when API is loaded and input is ready
  useEffect(() => {
    if (!autocompleteLoaded || !inputRef.current || autocompleteRef.current) {
      return;
    }
    
    try {
      // Configure autocomplete for cities/regions (NOT addresses or gyms)
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['(regions)'], // Cities, neighborhoods, suburbs, postal codes
        fields: ['place_id', 'formatted_address', 'geometry', 'name', 'address_components']
      });
      
      // Handle place selection
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        
        if (!place.geometry) {
          // User typed but didn't select - allow manual entry
          return;
        }
        
        // Extract city/area name for display
        let displayLabel = place.name || place.formatted_address;
        
        // Try to build a cleaner label from address components
        if (place.address_components) {
          const city = place.address_components.find(c => 
            c.types.includes('locality') || c.types.includes('sublocality')
          );
          const state = place.address_components.find(c => 
            c.types.includes('administrative_area_level_1')
          );
          
          if (city && state) {
            displayLabel = `${city.long_name}, ${state.short_name}`;
          } else if (city) {
            displayLabel = city.long_name;
          }
        }
        
        // Update state with place data
        setLocationLabel(displayLabel);
        setGooglePlaceId(place.place_id);
        setLat(place.geometry.location.lat());
        setLng(place.geometry.location.lng());
      });
    } catch (err) {
      console.error('[CreateClub] Failed to initialize autocomplete:', err);
      console.error('[CreateClub] Failed to initialize autocomplete:', err);
    }
  }, [autocompleteLoaded]);
  
  // Handle manual typing (clears geo data)
  const handleLocationChange = useCallback((e) => {
    const value = e.target.value;
    setLocationLabel(value);
    
    // If user types manually after selecting, clear geo data
    // (will be re-set if they select from dropdown)
    if (googlePlaceId) {
      setGooglePlaceId(null);
      setLat(null);
      setLng(null);
    }
  }, [googlePlaceId]);
  
  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!name.trim()) {
      setError('Club name is required');
      return;
    }
    if (!locationLabel.trim()) {
      setError('City or area is required');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        name: name.trim(),
        location_label: locationLabel.trim()
      };
      
      // Include geo metadata only if from autocomplete
      if (googlePlaceId) {
        payload.google_place_id = googlePlaceId;
        payload.lat = lat;
        payload.lng = lng;
      }
      
      const res = await axios.post(
        `${BACKEND_URL}/api/clubs`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCreatedClub(res.data);
      toast.success('Run Club created!');
    } catch (err) {
      console.error('Failed to create club:', err);
      console.error('Failed to create club:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail[0]?.msg || 'Validation error');
      } else {
        setError('Failed to create club. Please try again.');
      }
    } finally {
      setCreating(false);
    }
  };
  
  const handleCopyLink = async () => {
    if (!createdClub?.migration_link) return;
    
    try {
      await navigator.clipboard.writeText(createdClub.migration_link);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };
  
  // Redirect if not logged in
  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Login Required</h1>
          <p className="text-zinc-600 mb-6">You need to be logged in to create a Run Club.</p>
          <Button onClick={() => navigate('/login')} className="bg-orange-500 hover:bg-orange-600">
            Login
          </Button>
        </Card>
      </div>
    );
  }
  
  // Success state - show migration link
  if (createdClub) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 mb-2">Club Created!</h1>
            <p className="text-zinc-600">
              Share this link with your basketball crew to bring them into Open Runs.
            </p>
          </div>
          
          {/* Club Details */}
          <div className="bg-zinc-50 rounded-lg p-4 mb-6">
            <h2 className="font-semibold text-zinc-900 mb-1">{createdClub.name}</h2>
            <p className="text-sm text-zinc-500">{createdClub.location_label || 'Location not set'}</p>
          </div>
          
          {/* Migration Link */}
          <div className="mb-6">
            <Label className="text-sm text-zinc-500 mb-2 block">Migration Link</Label>
            <div className="flex gap-2">
              <Input
                value={createdClub.migration_link}
                readOnly
                className="bg-zinc-100 text-sm font-mono"
                data-testid="migration-link"
              />
              <Button
                onClick={handleCopyLink}
                variant="outline"
                className="shrink-0"
                data-testid="copy-link-btn"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          
          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={() => navigate(`/club/${createdClub.slug}`)}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="view-club-btn"
            >
              <Users className="w-4 h-4 mr-2" />
              View Your Club
            </Button>
            <Button
              onClick={() => navigate('/host')}
              variant="outline"
              className="w-full"
            >
              Create a Run for This Club
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  
  // Creation form
  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <div className="bg-zinc-900 text-white">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <button 
            onClick={() => guardedLeave(goBack)}
            className="flex items-center text-zinc-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <h1 className="text-2xl font-bold">Create a Run Club</h1>
          <p className="text-zinc-400 mt-1">
            Bring your basketball crew from GroupMe into Open Runs
          </p>
        </div>
      </div>
      
      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="p-6">
          <form onSubmit={handleCreate} className="space-y-6">
            {/* Club Name */}
            <div>
              <Label htmlFor="club-name" className="text-sm font-medium text-zinc-700">
                Club Name
              </Label>
              <Input
                id="club-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Lincoln Park Hoopers"
                className="mt-1"
                maxLength={50}
                data-testid="club-name-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                {name.length}/50 characters
              </p>
            </div>
            
            {/* City or Area - Phase 10A.1 */}
            <div>
              <Label htmlFor="location" className="text-sm font-medium text-zinc-700">
                City or Area
              </Label>
              <div className="relative mt-1">
                <Input
                  ref={inputRef}
                  id="location"
                  value={locationLabel}
                  onChange={handleLocationChange}
                  placeholder="e.g., Lincoln Park, Chicago"
                  className="pl-9"
                  maxLength={100}
                  data-testid="club-location-input"
                  autoComplete="off"
                />
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                City, suburb, or neighborhood where your crew plays. Start typing for suggestions.
              </p>
              {googlePlaceId && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Location verified
                </p>
              )}
            </div>
            
            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
            
            {/* Info */}
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex gap-3">
                <Link className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Migration Link</p>
                  <p className="text-sm text-blue-700 mt-1">
                    After creating your club, you'll get a shareable link. Send it to your crew—when they tap it, they'll join your club automatically after signing up.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Submit */}
            <Button
              type="submit"
              disabled={creating || !name.trim() || !locationLabel.trim()}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="create-club-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Run Club'
              )}
            </Button>
          </form>
        </Card>
      </div>
      <DiscardChangesDialog
        open={promptOpen}
        onKeepEditing={cancelLeave}
        onDiscard={confirmLeave}
      />
    </div>
  );
};

export default CreateClub;
